/**
 * Pair flow — 3 steps required by the 2026-05-19 spec:
 *   1. Enter the 6-char pair code
 *   2. Take a selfie (mandatory) — watermarked with date + code
 *   3. Upload national ID card (optional, can skip)
 *
 * Each pairing event writes a user_line_history row with the captured
 * proofs so we can answer "ตอนไหน userId อะไร" later. Existing users
 * paired before this change won't have proofs — that's expected.
 */
import { useRef, useState } from 'react';
import { Camera, CreditCard, ArrowRight, Check } from 'lucide-react';
import dayjs from 'dayjs';
import { apiFetch, uploadPairFile } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import { getIDToken } from '../lib/liff';
import { watermarkImage } from '../lib/watermark';
import type { AuthProfile } from '@payroll/shared';

type Step = 'code' | 'selfie' | 'id_card' | 'submitting';

export function Pair() {
  // ลิงก์เชิญ (จากปุ่ม "คัดลอกรหัสส่งให้พนักงาน") แนบ ?tenant=&code= มาด้วย
  // → เติมให้อัตโนมัติ พนักงานไม่ต้องพิมพ์รหัสเอง
  const initialParams = new URLSearchParams(window.location.search);
  // ?add=1 = ผูกบริษัทเพิ่มทั้งที่ล็อกอินอยู่ (มาจากปุ่ม "เพิ่มบริษัท") — แสดงหัวข้อ
  // + ปุ่มยกเลิกให้กลับได้ ไม่ค้าง. ปกติ (ยังไม่มีบริษัท) = "ผูกบัญชี LINE" เหมือนเดิม
  const isAddMode = initialParams.get('add') === '1';
  const [step, setStep] = useState<Step>('code');
  const [tenantCode, setTenantCode] = useState((initialParams.get('tenant') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, ''));
  const [code, setCode] = useState((initialParams.get('code') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6));
  const [selfiePath, setSelfiePath] = useState<string | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [idCardPath, setIdCardPath] = useState<string | null>(null);
  const [idCardPreview, setIdCardPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selfieRef = useRef<HTMLInputElement>(null);
  const idCardRef = useRef<HTMLInputElement>(null);
  const setAuth = useAuthStore((s) => s.setAuth);

  // Pairing uploads happen before the Worker JWT exists, so they go through
  // the idToken-gated /auth/pair-upload route (needs the entered tenant_code).
  function pairUploadArgs(): { idToken: string; tenantCode: string } {
    const idToken = getIDToken();
    if (!idToken) throw new Error('ไม่พบ idToken — กรุณาเปิดในแอป LINE');
    return { idToken, tenantCode: tenantCode.toUpperCase() };
  }

  async function handleSelfie(file: File) {
    setBusy(true); setError(null);
    try {
      const stamp = dayjs().format('D MMM YYYY · HH:mm');
      const watermarked = await watermarkImage({
        source: file,
        lines: ['ภาพถ่ายเพื่อยืนยันตัวตน', `รหัส: ${code}`, stamp],
      });
      const path = await uploadPairFile(watermarked, pairUploadArgs(), `pair_selfie_${Date.now()}.jpg`);
      setSelfiePath(path);
      setSelfiePreview(URL.createObjectURL(watermarked));
      setStep('id_card');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'อัปโหลดล้มเหลว');
    } finally {
      setBusy(false);
    }
  }

  async function handleIdCard(file: File) {
    setBusy(true); setError(null);
    try {
      const path = await uploadPairFile(file, pairUploadArgs(), `pair_id_${Date.now()}.${file.name.split('.').pop() ?? 'jpg'}`);
      setIdCardPath(path);
      setIdCardPreview(URL.createObjectURL(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'อัปโหลดล้มเหลว');
    } finally {
      setBusy(false);
    }
  }

  async function finalSubmit() {
    if (!selfiePath) {
      setError('ยังไม่ได้ถ่ายภาพ selfie');
      setStep('selfie');
      return;
    }
    setStep('submitting'); setBusy(true); setError(null);
    try {
      const idToken = getIDToken();
      if (!idToken) throw new Error('ไม่พบ idToken — กรุณาเปิดในแอป LINE');
      const result = await apiFetch<{ jwt: string; profile: AuthProfile }>('/auth/pair', {
        method: 'POST',
        body: {
          idToken,
          tenant_code: tenantCode.toUpperCase(),
          code: code.toUpperCase(),
          selfie_path: selfiePath,
          national_id_path: idCardPath ?? undefined,
        },
        skipAuth: true,
      });
      setAuth(result.jwt, result.profile);
      window.location.href = '/';
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ผิดพลาด');
      setStep('id_card');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-sm w-full p-6">
        <h2 className="text-xl font-bold text-center mb-1">{isAddMode ? 'เพิ่มบริษัท' : 'ผูกบัญชี LINE'}</h2>
        {isAddMode && (
          <p className="text-xs text-gray-500 text-center mb-1">
            ใส่รหัสบริษัทใหม่ที่ต้องการเพิ่ม — เสร็จแล้วสลับไปมาได้จากเมนูบริษัท
          </p>
        )}

        {/* Stepper */}
        <div className="flex items-center justify-center gap-1 my-3 text-xs">
          {(['code', 'selfie', 'id_card'] as const).map((s, i) => {
            const idx = ['code', 'selfie', 'id_card'].indexOf(step);
            const stepIdx = i;
            const done = idx > stepIdx || (step === 'id_card' && stepIdx <= 2);
            const current = idx === stepIdx;
            return (
              <div key={s} className="flex items-center gap-1">
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    done && !current ? 'bg-green-500 text-white' : current ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {done && !current ? <Check className="w-3.5 h-3.5" /> : stepIdx + 1}
                </span>
                {stepIdx < 2 && <div className="w-4 h-0.5 bg-gray-200" />}
              </div>
            );
          })}
        </div>

        {step === 'code' && (
          <>
            <p className="text-sm text-gray-600 text-center mb-4">
              กรอกรหัสบริษัท + รหัสจับคู่ที่ได้รับจากเจ้าของระบบ
            </p>
            <label className="label">รหัสบริษัท</label>
            <input
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              maxLength={12}
              placeholder="ABCDEF"
              className="input text-center text-2xl font-mono tracking-widest mb-3"
            />
            <label className="label">รหัสจับคู่</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="123456"
              className="input text-center text-2xl font-mono tracking-widest"
            />
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
            <button
              onClick={() => {
                if (tenantCode.length < 4) {
                  setError('กรอกรหัสบริษัท');
                  return;
                }
                if (code.length !== 6) {
                  setError('กรอกรหัสจับคู่ให้ครบ 6 ตัว');
                  return;
                }
                setError(null);
                setStep('selfie');
              }}
              className="btn-primary w-full mt-4"
            >
              ถัดไป <ArrowRight className="w-4 h-4" />
            </button>
            {isAddMode && (
              <button
                onClick={() => { window.location.href = '/'; }}
                className="btn-secondary w-full mt-2"
              >
                ยกเลิก
              </button>
            )}
          </>
        )}

        {step === 'selfie' && (
          <>
            <p className="text-sm text-gray-600 text-center mb-2">ถ่ายภาพใบหน้าเพื่อยืนยันตัวตน</p>
            <p className="text-xs text-gray-500 text-center mb-4">ภาพจะถูกบันทึกพร้อมรหัสและวันเวลา</p>
            {selfiePreview && (
              <img src={selfiePreview} alt="selfie" className="w-full rounded-lg border mb-3" />
            )}
            <button
              onClick={() => selfieRef.current?.click()}
              disabled={busy}
              className="btn-primary w-full"
            >
              <Camera className="w-5 h-5" /> {busy ? 'กำลังอัปโหลด…' : selfiePreview ? 'ถ่ายใหม่' : 'ถ่ายรูป'}
            </button>
            <input
              ref={selfieRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleSelfie(f);
                e.target.value = '';
              }}
            />
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          </>
        )}

        {step === 'id_card' && (
          <>
            <p className="text-sm text-gray-600 text-center mb-2">ถ่าย/อัปโหลด บัตรประชาชน</p>
            <p className="text-xs text-gray-500 text-center mb-4">
              ไม่บังคับ — ข้ามได้ถ้ายังไม่สะดวก
            </p>
            {idCardPreview && (
              <img src={idCardPreview} alt="id card" className="w-full rounded-lg border mb-3" />
            )}
            <button
              onClick={() => idCardRef.current?.click()}
              disabled={busy}
              className="btn-secondary w-full mb-2"
            >
              <CreditCard className="w-5 h-5" /> {busy ? 'กำลังอัปโหลด…' : idCardPath ? 'เปลี่ยนรูป' : 'แนบรูปบัตรประชาชน'}
            </button>
            <input
              ref={idCardRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleIdCard(f);
                e.target.value = '';
              }}
            />
            <button onClick={finalSubmit} disabled={busy} className="btn-primary w-full mt-1">
              {idCardPath ? 'ยืนยันและผูกบัญชี' : 'ข้าม / ผูกบัญชี'}
            </button>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          </>
        )}

        {step === 'submitting' && (
          <div className="text-center py-8">
            <div className="text-3xl mb-2">⏳</div>
            <p className="text-sm">กำลังผูกบัญชี…</p>
          </div>
        )}
      </div>
    </div>
  );
}
